import type { OutgoingEvent } from 'jssip/lib/RTCSession';

import type { SipResponseWithHeaders } from 'features/sip/handlers/';
import type { Call } from 'features/call/call-base/interfaces';
import { deafIpTracebackKnown } from 'features/call/call-base/store';
import { sendAnalyticsInfo } from 'features/analytics/helpers';
import { CallPartialKeys, sipDeafProtocol } from 'features/sip/constants';
import { setIsVcoRequested } from 'features/vco/store';
import type { AppDispatch } from 'features/app/store/store';

/**
 * Safely parses a string to an integer, returning undefined if invalid
 */
const parseIntSafe = (value: string | undefined): number | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? undefined : parsed;
};

/**
 * Parses SIP INFO attributes from SDP body
 */
export const getSipInfoAttributes = (
  sdp: string
): Record<string, string> | undefined => {
  try {
    const pattern = /a=X-SVRS-INFO:SInfo:(.*)/;
    const sipInfoMatch = pattern.exec(sdp ?? '');

    if (sipInfoMatch?.[1]) {
      const sipInfoValues = sipInfoMatch[1]
        .split(';')
        .map((param: string) => param.trim());

      return sipInfoValues[0]
        .split('|')
        .reduce((acc: Record<string, string>, curr: string) => {
          const key = curr.slice(0, 4);
          const value = curr.slice(4);
          return {
            ...acc,
            ...(CallPartialKeys[key] ? { [CallPartialKeys[key]]: value } : {}),
          };
        }, {});
    }

    return undefined;
  } catch (error) {
    console.error('Failed to parse SIP INFO attributes:', error);
    return undefined;
  }
};

/**
 * Extracts and transforms call properties from SIP INFO attributes
 */
export const getCallPartialFromSipInfoAttributes = (
  event: OutgoingEvent
): Partial<Call> | undefined => {
  try {
    const response = event.response as SipResponseWithHeaders;
    const callProperties = getSipInfoAttributes(response.body);

    if (!callProperties) {
      return undefined;
    }

    const {
      CarryOverType,
      CoreServicesUserId,
      CoreRingGroupOwnerUserId,
      IsVcoRequested,
      ...otherProperties
    } = callProperties;

    const carryOverTypeNum = parseIntSafe(CarryOverType);
    const coreServicesUserIdNum = parseIntSafe(CoreServicesUserId);
    const coreRingGroupOwnerUserIdNum = parseIntSafe(CoreRingGroupOwnerUserId);
    const isVcoRequestedNum = parseIntSafe(IsVcoRequested);

    return {
      ...otherProperties,
      DeafProtocol: sipDeafProtocol,
      ...(carryOverTypeNum !== undefined && {
        CarryOverType: carryOverTypeNum,
      }),
      ...(coreServicesUserIdNum !== undefined && {
        CoreServicesUserId: coreServicesUserIdNum,
      }),
      ...(coreRingGroupOwnerUserIdNum !== undefined && {
        CoreRingGroupOwnerUserId: coreRingGroupOwnerUserIdNum,
      }),
      ...(isVcoRequestedNum !== undefined && {
        IsVcoRequested: Boolean(isVcoRequestedNum),
      }),
    };
  } catch (error) {
    console.error('Failed to extract call partial from SIP INFO:', error);
    return undefined;
  }
};

/**
 * Extracts IP address from Call-Info header
 */
export const getIpAddressFromCallInfo = (
  event: OutgoingEvent
): Partial<Call> | undefined => {
  try {
    const response = event.response as SipResponseWithHeaders;
    const headers = response.headers;
    if (!headers?.['Call-Info']?.[0]) {
      return undefined;
    }

    const callInfoHeader = headers['Call-Info'][0]?.raw;
    if (!callInfoHeader) {
      return undefined;
    }

    // Extract IP address from <sip:IP_ADDRESS> format, with or without purpose parameter
    const ipMatch = /<sip:([^>]+)>/.exec(callInfoHeader ?? '');
    if (!ipMatch?.[1]) {
      return undefined;
    }

    return {
      DeafIpTraceback: ipMatch[1],
    };
  } catch (error) {
    console.error('Failed to extract IP address from Call-Info header:', error);
    return undefined;
  }
};

/**
 * Merges call data from SIP INFO attributes and Call-Info header
 */
export const getMergedCallData = (
  event: OutgoingEvent,
  skipIpExtraction: boolean
): {
  mergedData: Partial<Call> | undefined;
  ipFromCallInfo: Partial<Call> | undefined;
} => {
  const partialCallFromSipInfo = getCallPartialFromSipInfoAttributes(event);
  const partialCallFromIpAddress = skipIpExtraction
    ? undefined
    : getIpAddressFromCallInfo(event);

  const mergedPartialCall = {
    ...partialCallFromSipInfo,
    ...partialCallFromIpAddress,
  };

  // Return undefined if there's nothing to update
  if (Object.keys(mergedPartialCall).length === 0) {
    return {
      mergedData: undefined,
      ipFromCallInfo: partialCallFromIpAddress,
    };
  }

  return {
    mergedData: mergedPartialCall,
    ipFromCallInfo: partialCallFromIpAddress,
  };
};

/**
 * Updates local Redux state for VCO request status
 */
export const handleVcoStateUpdate = (
  mergedCall: Partial<Call>,
  dispatch: AppDispatch
) => {
  if (mergedCall.IsVcoRequested !== undefined) {
    dispatch(setIsVcoRequested(mergedCall.IsVcoRequested));
  }
};

/**
 * Updates local Redux state for deaf IP traceback and sends analytics if needed
 */
export const handleIpTracebackUpdate = (
  mergedCall: Partial<Call>,
  partialCallFromIpAddress: Partial<Call> | undefined,
  dispatch: AppDispatch
) => {
  if (mergedCall.DeafIpTraceback !== undefined) {
    dispatch(deafIpTracebackKnown(mergedCall.DeafIpTraceback));

    // Send analytics if the IP came from Call-Info header (not SIP INFO)
    if (partialCallFromIpAddress?.DeafIpTraceback) {
      dispatch(
        sendAnalyticsInfo({
          event: 'DeafIpTracebackUpdated',
          Message: 'Deaf IP traceback updated for call',
        })
      );
    }
  }
};
